import {
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
} from '@reduxjs/toolkit';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {Platform} from 'react-native';
import {ShowToast} from '../../Common/common';
import {resetState} from '../services/authSlice';

/**
 * Helper function to clear storage on logout/session expiry
 */
const clearStorage = async () => {
  try {
    if (Platform.OS === 'web') {
      // Web: clear sessionStorage keys starting with "report_"
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('report_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      localStorage.removeItem('token');
      localStorage.removeItem('authData');
      localStorage.removeItem('multiTab.tabs');
      localStorage.removeItem('multiTab.current');
    } else {
      // React Native: clear AsyncStorage
      await AsyncStorage.multiRemove([
        'token',
        'authData',
        'multiTab.tabs',
        'multiTab.current',
      ]);
      // Clear report_ keys
      const allKeys = await AsyncStorage.getAllKeys();
      const reportKeys = allKeys.filter(key => key.startsWith('report_'));
      if (reportKeys.length > 0) {
        await AsyncStorage.multiRemove(reportKeys);
      }
    }
  } catch (error) {
    console.error('Error clearing storage:', error);
  }
};

/**
 * Helper function to parse error data from various formats
 */
const parseErrorData = (rawData: any): any => {
  if (typeof rawData === 'string') {
    try {
      return JSON.parse(rawData);
    } catch {
      return rawData;
    }
  }
  return rawData;
};

/**
 * Helper function to extract error message from various formats
 */
const extractErrorMessage = (errorData: any): string => {
  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object' && errorData !== null) {
    // Check for message property
    if (errorData.message) {
      return typeof errorData.message === 'string'
        ? errorData.message
        : JSON.stringify(errorData.message);
    }
    // Check for error property
    if (errorData.error) {
      return typeof errorData.error === 'string'
        ? errorData.error
        : errorData.error?.message || JSON.stringify(errorData.error);
    }
    // Check for data property
    if (errorData.data) {
      return typeof errorData.data === 'string'
        ? errorData.data
        : errorData.data?.message || JSON.stringify(errorData.data);
    }
    // If still an object, stringify it
    return JSON.stringify(errorData);
  }

  return 'An unknown error occurred';
};

/**
 * Check if error is a session expired error
 */
const isSessionExpiredError = (errorData: any, rawData: any): boolean => {
  return (
    (typeof errorData === 'object' &&
      errorData !== null &&
      errorData.message === 'Session expired or invalid. Please login again.' &&
      errorData.reason === 'Session not found') ||
    (typeof rawData === 'string' &&
      rawData.includes('Session expired or invalid. Please login again.') &&
      rawData.includes('Session not found'))
  );
};

/**
 * RTK Query error logger middleware
 * Handles various HTTP error codes and session expiry
 */
export const rtkQueryErrorLogger: Middleware =
  (api: MiddlewareAPI) => next => (action: any) => {
    if (isRejectedWithValue(action)) {
      const rawData = (action.payload as any)?.data;
      const errorData = parseErrorData(rawData);
      const statusCode =
        (action.payload as any)?.originalStatus ??
        (action.payload as any)?.status;

      // Check for session expired error
      if (isSessionExpiredError(errorData, rawData)) {
        ShowToast('Your session has expired. Please login again.', 'error');
        clearStorage();
        api.dispatch(resetState());
        return next(action);
      }

      // Handle specific status codes
      switch (statusCode) {
        case 500: {
          const errorMessage = extractErrorMessage(errorData);
          const finalMessage =
            typeof errorMessage === 'string' && errorMessage.trim()
              ? errorMessage
              : 'Internal server error. Please try again later.';
          ShowToast(finalMessage, 'error');
          break;
        }

        case 400: {
          let errorMessage: string;
          if (
            errorData &&
            typeof errorData === 'object' &&
            Array.isArray(errorData.errors)
          ) {
            // Handle validation errors with errors array
            const validationMessages = errorData.errors
              .map((err: any) => {
                const fieldName = err.path || '';
                const fieldMsg = err.msg || '';
                return fieldName ? `${fieldName}: ${fieldMsg}` : fieldMsg;
              })
              .filter((msg: string) => msg)
              .join(', ');

            errorMessage =
              validationMessages || errorData.message || 'Validation failed';
          } else {
            errorMessage = extractErrorMessage(errorData);
          }
          ShowToast(errorMessage, 'error');
          break;
        }

        case 401: {
          // Check if this is a session expired error to avoid duplicate notifications
          const statusCode401Data = (action.payload as any)?.data;
          const statusCode401ErrorData = parseErrorData(statusCode401Data);
          const isSessionExpired401 = isSessionExpiredError(
            statusCode401ErrorData,
            statusCode401Data,
          );

          // Only show generic 401 error if it's not a session expired error
          if (!isSessionExpired401) {
            ShowToast(extractErrorMessage(statusCode401ErrorData), 'error');
          }

          clearStorage();
          api.dispatch(resetState());
          break;
        }

        case 301: {
          const errorMessage = extractErrorMessage(errorData);
          const finalMessage =
            typeof errorMessage === 'string' && errorMessage.trim()
              ? errorMessage
              : 'Resource has been moved permanently.';
          ShowToast(finalMessage, 'error');
          break;
        }

        case 2001: {
          ShowToast((action.payload as any).data, 'success');
          break;
        }

        default: {
          // fetchBaseQuery reports connectivity/timeout/parsing failures via
          // string statuses ('FETCH_ERROR', 'TIMEOUT_ERROR', 'PARSING_ERROR',
          // 'CUSTOM_ERROR') instead of a numeric HTTP code - previously none
          // of these matched any case above, so the user got no feedback at
          // all and the action appeared to silently do nothing.
          if (typeof statusCode === 'string') {
            ShowToast(
              'Network error. Please check your connection and try again.',
              'error',
            );
          }
          break;
        }
      }
    }

    return next(action);
  };

